/**
 * What the taskbar's app well renders, derived from shell state.
 *
 * ONE ordered list of entries — an app on the rail, or a folder holding apps —
 * so the TaskBar can map over it and know nothing about pins, running
 * instances or persistence. `App.tsx` calls this and decorates the result
 * (per-app `canEdit`, from a permission query core-ui cannot make).
 *
 * ── Dock order ───────────────────────────────────────────────────────────
 *
 * Pinned entries first, in `pinOrder` — apps and folders interleaved, since a
 * folder takes the slot of the app it was created on — then running-but-
 * unpinned apps in the order they were opened.
 *
 * Before folders the well listed RUNNING apps first, which made a pinned app's
 * position depend on whether it happened to be open. That was tolerable for
 * single tiles and is not for a folder: a group that jumps to the top every
 * time one of its members is launched cannot be found twice. Dock semantics
 * (macOS, Windows, Discord) are what people expect of a rail with pins.
 *
 * A pin whose `folderId` names a folder that is not in the list is drawn on
 * the rail — the defensive reading, since the alternative is an app that is
 * pinned and visible nowhere.
 */
import type { MiniappIconSpec, FolderColorKey } from '@papan/types'
import type { ShellAppInstance, ShellFolder, ShellPin } from '../ShellManager.js'

export type RailAppKind = 'miniapp' | 'html'

export interface RailApp {
  type: 'app'
  kind: RailAppKind
  ref: string
  title?: string
  icon?: MiniappIconSpec | null
  iconRev?: string | null
  /** This app's window is the focused one. */
  active: boolean
  /** A keep-alive window is mounted (running dot; has a close action). */
  running: boolean
  /** #538 — running, but its window hasn't loaded yet in this page load (it loads when first opened, as a
   *  browser's restored tab): the running dot is drawn faint. Set by the host. */
  asleep?: boolean
  /** Kept on the dock regardless of running-state. */
  pinned: boolean
  /** The folder this app is filed in; null on the rail. */
  folderId: string | null
  /** Mini-apps only: the viewer may edit the icon. Decorated by the caller. */
  canEdit?: boolean
  /** How many of the app's run sources' last queued `db_run` writes FAILED, undismissed — counted only for an app the
   *  viewer can edit (the server answers no one else). Decorated by the caller; absent = none. */
  writeFailures?: number
}

export interface RailFolder {
  type: 'folder'
  id: string
  /** '' = unnamed. */
  title: string
  color: FolderColorKey | null
  /** In `pinOrder`. Never empty — an empty folder is not rendered. */
  members: RailApp[]
  /** A member is the focused window. */
  active: boolean
  /** A member is running. */
  running: boolean
}

export type RailEntry = RailApp | RailFolder

const isUserKind = (kind: string): kind is RailAppKind => kind === 'miniapp' || kind === 'html'
const key = (kind: string, ref: string) => `${kind}:${ref}`

export function composeRailEntries(input: {
  running: ShellAppInstance[]
  pins: ShellPin[]
  folders: ShellFolder[]
  activeRef: string | null
}): RailEntry[] {
  const { running, pins, folders, activeRef } = input
  const runningByKey = new Map<string, ShellAppInstance>()
  for (const a of running) if (isUserKind(a.kind)) runningByKey.set(key(a.kind, a.ref), a)
  const knownFolders = new Set(folders.map(f => f.id))

  // A running instance carries the freshest title/icon (resolved lazily by
  // the window); a pin's copy is the snapshot from pin time. Prefer live.
  const appFromPin = (p: ShellPin): RailApp => {
    const live = runningByKey.get(key(p.kind, p.ref))
    return {
      type: 'app',
      kind: p.kind as RailAppKind,
      ref: p.ref,
      title: live?.title ?? p.title,
      icon: live?.icon !== undefined ? live.icon : p.icon,
      iconRev: live?.icon !== undefined ? (live.iconRev ?? null) : p.iconRev,
      active: p.ref === activeRef,
      running: live !== undefined,
      pinned: true,
      folderId: p.folderId && knownFolders.has(p.folderId) ? p.folderId : null,
    }
  }

  const pinnedApps = pins.filter(p => isUserKind(p.kind)).map(appFromPin)
  const byOrder = new Map<RailApp, number>()
  pins.forEach(p => { const a = pinnedApps.find(x => x.kind === p.kind && x.ref === p.ref); if (a) byOrder.set(a, p.pinOrder) })

  const membersOf = new Map<string, RailApp[]>()
  for (const a of pinnedApps) {
    if (!a.folderId) continue
    const list = membersOf.get(a.folderId) ?? []
    list.push(a)
    membersOf.set(a.folderId, list)
  }

  type Slot = { order: number; tie: number; entry: RailEntry }
  const slots: Slot[] = []
  for (const a of pinnedApps) {
    if (a.folderId) continue
    slots.push({ order: byOrder.get(a) ?? 0, tie: 1, entry: a })
  }
  for (const f of folders) {
    const members = (membersOf.get(f.id) ?? []).sort((x, y) => (byOrder.get(x) ?? 0) - (byOrder.get(y) ?? 0))
    if (members.length === 0) continue
    slots.push({
      order: f.pinOrder,
      tie: 0,   // a folder created on an app's slot wins the tie against a pin still on it
      entry: {
        type: 'folder',
        id: f.id,
        title: f.title,
        color: f.color,
        members,
        active: members.some(m => m.active),
        running: members.some(m => m.running),
      },
    })
  }
  slots.sort((x, y) => x.order - y.order || x.tie - y.tie || tieKey(x.entry).localeCompare(tieKey(y.entry)))

  const pinnedKeys = new Set(pinnedApps.map(a => key(a.kind, a.ref)))
  const runningUnpinned: RailApp[] = running
    .filter(a => isUserKind(a.kind) && !pinnedKeys.has(key(a.kind, a.ref)))
    .map(a => ({
      type: 'app',
      kind: a.kind as RailAppKind,
      ref: a.ref,
      title: a.title,
      icon: a.icon,
      iconRev: a.iconRev,
      active: a.ref === activeRef,
      running: true,
      pinned: false,
      folderId: null,
    }))

  return [...slots.map(s => s.entry), ...runningUnpinned]
}

/** A STABLE tiebreaker string, not a rail key — deliberately NOT the same
 *  format as `railKeyOfPin` (`app:<kind>:<ref>`), and renamed from `entryKey`
 *  because sharing that name with TaskBar's identically-named helper while
 *  producing a different string is a trap. Only ever compared with itself, to
 *  make the sort deterministic when two entries claim one slot. */
const tieKey = (e: RailEntry) => (e.type === 'folder' ? `folder:${e.id}` : key(e.kind, e.ref))

/** Every app an entry list renders, folders flattened — for callers that
 *  decorate per app (permissions) or need "is anything running". */
export function railApps(entries: RailEntry[]): RailApp[] {
  const out: RailApp[] = []
  for (const e of entries) {
    if (e.type === 'folder') out.push(...e.members)
    else out.push(e)
  }
  return out
}
